import { vec2 } from "@tlaukkan/tsm";
import { Entity } from "ecsy";
import { queryRadius } from "./box2dUtils";
import { Chained, Antibody, Virus, BloodCell, ElementInfo, Transform, IsBeingDragged } from "./components";
import { getEntityPlayer } from "./composition";
import { CellCategory } from "./constants";
import { getEntityById } from "./entityFactory";
import { Game } from "./game";
import { GameElement } from "./gameElementTypes";
import { effectQueue } from "./systems/triggeredEffectSystem";
import { FrozenComponent, getConnectedCells, uniformDistrubtion } from "./utils";

export enum TargetType {
	Self,
	Team,
	Range,
	Chained,
	Connected,
	Other, //other cells relevent to the trigger, eg, the thing you collided thing
	SelfIfOther, //If the other cell passes filters, than target self. Kindof a hack for the grow wall
};

export enum TargetFilterType {
	ImmuneCell,
	Virus,
	BloodCell,
	ElementType,
	Category,
	Random,
	Nearest,
	NotSelf,
};

export enum TriggerScope {
	Permanent,
	OneShot
};

export type SimpleSelector = {
	type: TargetType.Team | TargetType.Self | TargetType.Other | TargetType.Chained | TargetType.Connected | TargetType.SelfIfOther;
};

export type RangeSelector = {
	type: TargetType.Range;
	range: number;
};

export type TargetSelector = SimpleSelector | RangeSelector;

type SimpleFilter = {
	type: TargetFilterType.BloodCell | TargetFilterType.Virus | TargetFilterType.ImmuneCell | TargetFilterType.Nearest | TargetFilterType.NotSelf;
}

type RandomFilter = {
	type: TargetFilterType.Random;
	count: number;
}

type ElementTypeFilter = {
	type: TargetFilterType.ElementType;
	element: GameElement;
	count: number;
}

type CategoryFilter = {
	type: TargetFilterType.Category;
	category: CellCategory;
}

export type TargetFilter = SimpleFilter | RandomFilter | ElementTypeFilter | CategoryFilter;

export function triggerEffect<T>(effect: TriggeredEffect | TriggeredEffect[], source: Entity, other?: Entity) {
	if (effect instanceof Array) {
		for (const e of effect) {
			effectQueue.push({ effect: e, source, other });
		}
	} else {
		effectQueue.push({ effect, source, other });
	}
}

export type TriggeredEffect = {
	component: FrozenComponent,
	targetSelector?: TargetSelector,
	filters?: Array<TargetFilter>,
	scope?: TriggerScope
}

//TODO: Range queries do not utilize the target system! Big optimization issue if a range query is run every frame!
export function selectAndFilter(selector: TargetSelector, filters: Array<TargetFilter>, creator: Entity, game: Game, other?: Entity, targets = new Array<Entity>()) {

	switch (selector.type) {
		case TargetType.Self: {
			targets.push(creator);
			break;
		}
		case TargetType.Other: {
			if (other !== undefined) {
				targets.push(other);
			}
			break
		}
		case TargetType.SelfIfOther: {
			if (other !== undefined) {
				targets.push(other);
			}
			break
		}
		case TargetType.Team: {
			const player = getEntityPlayer(creator, game);
			if (player) {
				player.immuneCells
					.map(cellComp => getEntityById(cellComp.id))
					.filter(entity => entity !== null)
					.forEach(entity => targets.push(entity!));
			}
			break;
		}
		case TargetType.Range: {
			const transform = creator.getComponent(Transform);
			const hits = queryRadius(transform.position, selector.range, entity => {
				return entity.id !== creator.id
			});

			for (const hit of hits) {
				targets.push(hit);
			}
			break;
		}
		case TargetType.Chained: {
			if (creator.hasComponent(Chained) && !creator.hasComponent(IsBeingDragged)) {
				const chained = creator.getComponent(Chained);
				targets = targets.concat(chained.links);
			}
			break
		}
		case TargetType.Connected: {
			targets = targets.concat(getConnectedCells(creator));
			break;
		}
	}

	//Filter the targets
	let takeRandom = false;
	let takeNearest = false;
	for (const filter of filters) {
		switch (filter.type) {
			case TargetFilterType.ImmuneCell:
				targets = targets.filter(target => target.hasComponent(Antibody));
				break;
			case TargetFilterType.Virus:
				targets = targets.filter(target => target.hasComponent(Virus));
				break;
			case TargetFilterType.BloodCell:
				targets = targets.filter(target => target.hasComponent(BloodCell));
				break;
			case TargetFilterType.Category: {
				targets = targets
					.filter(target => target.hasComponent(ElementInfo))
					.filter(target => target.getComponent(ElementInfo).category === filter.category)
				break;
			}
			case TargetFilterType.ElementType: {
				const elementInfo = creator.getComponent(ElementInfo);
				const type = elementInfo.type;
				targets = targets.filter(target => target.getComponent(ElementInfo).type === type);
				break;
			}

			case TargetFilterType.Random:
				takeRandom = true;
				break;
			case TargetFilterType.Nearest:
				takeNearest = true;
				break;
			case TargetFilterType.NotSelf:
				targets = targets.filter(target => target.id !== creator.id);
				break;
		}
	}
	if (takeNearest) {
		const transform = creator.getComponent(Transform);
		let nearest: Entity | null = null;
		let nearestRange: number = Infinity;
		for (const target of targets) {
			const sqrDistance = vec2.squaredDistance(target.getComponent(Transform).position, transform.position);
			if (sqrDistance < nearestRange) {
				nearestRange = sqrDistance;
				nearest = target;
			}
		}
		if (nearest) {
			targets = [nearest];
		}
	}
	if (takeRandom) {
		const randomIdx = uniformDistrubtion(targets.length);
		targets = [targets[randomIdx]];
	}

	if (selector.type === TargetType.SelfIfOther && targets.length > 0) {
		targets = [creator];
	}

	return targets;
}

// export class TriggeredEffect {
// 	constructor(
// 		public frozenComponent: FrozenComponent,
// 		public targetSelector: TargetSelector = { type: TargetType.Self },
// 		public filters = new Array<TargetFilter>(),
// 		public scope = TriggeredComponentScope
// 	) { }
// }

//TODO: Could this replace everything?
// export class AddComponentEffect<T> extends TriggeredEffect {
// 	constructor(public frozenComponent: FrozenComponent<T>) {
// 	}
// 	super();
// }
// }

// export class SpawnEffect extends TriggeredEffect {
// 	constructor(public spawnee: GameElement) {
// 		super();
// 	}
// }

// export class HealEffect extends TriggeredEffect {
// 	constructor(
// 		public amount: number,
// 	) {
// 		super();
// 	}
// }
